import React from 'react';
import { useVisualizationStore } from '../store/visualizationStore';
import { getLinkEndpointId } from '../types/infrastructure';
import { calculateInfrastructureStats } from '../utils/infrastructureStats';

export const InfoPanel: React.FC = () => {
  const {
    selectedNode,
    hoveredNode,
    nodes,
    links,
    infrastructure
  } = useVisualizationStore();

  const displayNode = selectedNode || hoveredNode;
  const node = displayNode ? nodes.find(n => n.id === displayNode) : null;

  // Get node connections
  const getNodeConnections = (nodeId: string) => {
    return links.filter(link =>
      getLinkEndpointId(link.source) === nodeId || getLinkEndpointId(link.target) === nodeId
    );
  };

  // Get child nodes count
  const getChildrenCount = (nodeId: string) => {
    return nodes.filter(n => n.parent === nodeId).length;
  };

  // Format bandwidth
  const formatBandwidth = (bandwidth?: { value: number; unit: string }) => {
    if (!bandwidth) return 'Unknown';
    return `${bandwidth.value} ${bandwidth.unit}`;
  };

  // Get infrastructure statistics
  const stats = infrastructure ? calculateInfrastructureStats(infrastructure) : null;

  if (!node && !stats) {
    return (
      <div className="info-panel bg-white shadow-lg rounded-lg p-4 absolute bottom-4 right-4 z-10 w-80">
        <div className="text-center text-gray-500">
          {!infrastructure ? 'Load infrastructure data to get started' : 'Hover or select a node for details'}
        </div>
      </div>
    );
  }

  return (
    <div className="info-panel bg-white shadow-lg rounded-lg p-4 absolute bottom-4 right-4 z-10 w-80 max-h-96 overflow-y-auto">
      {/* Node Information */}
      {node && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedNode ? 'Selected' : 'Hovered'} Node
            </h3>
            {selectedNode && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                Selected
              </span>
            )}
          </div>

          {/* Basic Info */}
          <div className="space-y-2">
            <div>
              <span className="font-medium text-gray-600">Name:</span>
              <span className="ml-2 text-gray-900">{node.name}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Type:</span>
              <span className="ml-2 text-gray-900 capitalize">{node.type}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">ID:</span>
              <span className="ml-2 text-gray-900 font-mono text-sm">{node.id}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Position:</span>
              <span className="ml-2 text-gray-900">
                ({node.x.toFixed(1)}, {node.y.toFixed(1)})
              </span>
            </div>
          </div>

          {/* Hierarchy */}
          {(node.parent || node.children?.length) && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <h4 className="font-medium text-gray-700 mb-2">Hierarchy</h4>
              {node.parent && (
                <div className="text-sm">
                  <span className="text-gray-600">Parent:</span>
                  <span className="ml-2 text-gray-900">{node.parent}</span>
                </div>
              )}
              {node.children?.length && (
                <div className="text-sm">
                  <span className="text-gray-600">Children:</span>
                  <span className="ml-2 text-gray-900">{getChildrenCount(node.id)}</span>
                </div>
              )}
            </div>
          )}

          {/* Connections */}
          {(() => {
            const connections = getNodeConnections(node.id);
            return connections.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <h4 className="font-medium text-gray-700 mb-2">
                  Connections ({connections.length})
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {connections.slice(0, 5).map((connection, index) => (
                    <div key={index} className="text-sm bg-gray-50 p-2 rounded">
                      <div className="font-medium">
                        {getLinkEndpointId(connection.source) === node.id ? getLinkEndpointId(connection.target) : getLinkEndpointId(connection.source)}
                      </div>
                      {connection.protocol && (
                        <div className="text-gray-600">Protocol: {connection.protocol}</div>
                      )}
                      {connection.bandwidth && (
                        <div className="text-gray-600">
                          Bandwidth: {formatBandwidth(connection.bandwidth)}
                        </div>
                      )}
                    </div>
                  ))}
                  {connections.length > 5 && (
                    <div className="text-xs text-gray-500 text-center">
                      ... and {connections.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Metadata */}
          {node.metadata && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <h4 className="font-medium text-gray-700 mb-2">Metadata</h4>
              <div className="space-y-1">
                {Object.entries(node.metadata).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="text-gray-600 capitalize">{key}:</span>
                    <span className="ml-2 text-gray-900">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Infrastructure Statistics */}
      {stats && (
        <div className={node ? 'pt-4 border-t border-gray-200' : ''}>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Infrastructure Overview
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-2xl font-bold text-blue-600">{stats.totalInstances}</div>
              <div className="text-sm text-blue-700">Total Instances</div>
            </div>
            <div className="bg-green-50 p-3 rounded">
              <div className="text-2xl font-bold text-green-600">{stats.totalConnections}</div>
              <div className="text-sm text-green-700">Connections</div>
            </div>
            <div className="bg-purple-50 p-3 rounded">
              <div className="text-2xl font-bold text-purple-600">{stats.sites}</div>
              <div className="text-sm text-purple-700">Sites</div>
            </div>
            <div className="bg-orange-50 p-3 rounded">
              <div className="text-2xl font-bold text-orange-600">{stats.pods}</div>
              <div className="text-sm text-orange-700">Pods</div>
            </div>
            <div className="bg-red-50 p-3 rounded">
              <div className="text-2xl font-bold text-red-600">{stats.racks}</div>
              <div className="text-sm text-red-700">Racks</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-2xl font-bold text-gray-600">{stats.deviceTypes}</div>
              <div className="text-sm text-gray-700">Device Types</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};